import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AppWindow,
  Plus,
  Loader2,
  Star,
  Search,
  Sparkles,
  Code2,
  Lightbulb,
  Trash2,
} from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { operation } from "@/lib/api-client";
import { useAuth } from "@/hooks/use-auth";
import { ContextualHelpLink } from "@/components/help/contextual-help-link";
import { toast } from "sonner";

export const Route = createFileRoute("/apps/")({
  head: () => ({
    meta: [
      { title: "App Builder — AskBell" },
      {
        name: "description",
        content:
          "Generate sandboxed interactive apps and dashboards from your data using natural language.",
      },
    ],
  }),
  component: AppsIndexPage,
});

type AppRow = {
  id: string;
  title: string;
  description: string | null;
  kind: "spec" | "react";
  starred: boolean;
  updated_at: string;
};

type KindFilter = "all" | "spec" | "react";

function AppsIndexPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState<AppRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [pendingDelete, setPendingDelete] = useState<AppRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const loadVersion = useRef(0);
  const deleteLock = useRef(false);
  const starLocks = useRef(new Set<string>());
  const [starring, setStarring] = useState<Set<string>>(new Set());

  const fetchRows = async () => {
    const version = ++loadVersion.current;
    setLoading(true); setLoadError(null);
    if (!user) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      const result = await operation<{}, { apps: AppRow[] }>("app_list", {});
      if (version !== loadVersion.current) return;
      setRows(result.apps);
    } catch (error) {
      if (version === loadVersion.current) setLoadError(error instanceof Error ? error.message : "Couldn't load apps");
    }
    if (version === loadVersion.current) setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    void fetchRows();
    return () => { loadVersion.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const visible = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (kindFilter !== "all" && r.kind !== kindFilter) return false;
      if (!q) return true;
      return (
        r.title.toLowerCase().includes(q) ||
        (r.description ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, query, kindFilter]);

  const toggleStar = async (row: AppRow, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (starLocks.current.has(row.id)) return;
    starLocks.current.add(row.id); setStarring(new Set(starLocks.current));
    const next = !row.starred;
    try {
      await operation("app_set_star", { appId: row.id, starred: next });
      setRows((prev) => (prev ?? []).map((r) => r.id === row.id ? { ...r, starred: next } : r));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Couldn't update star");
    } finally { starLocks.current.delete(row.id); setStarring(new Set(starLocks.current)); }

  };

  const requestDelete = (row: AppRow, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPendingDelete(row);
  };

  const confirmDelete = async () => {
    if (!pendingDelete || deleteLock.current) return;
    deleteLock.current = true;
    setDeleting(true);
    const target = pendingDelete;
    try {
      await operation("app_delete", { appId: target.id });
      setRows((prev) => (prev ?? []).filter((r) => r.id !== target.id));
      setPendingDelete(null);
      toast.success("App deleted");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Couldn't delete app");
    } finally {
      deleteLock.current = false;
      setDeleting(false);
    }

  };

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl px-6 py-8">
          {/* Header */}
          <div className="mb-6 flex flex-col sm:flex-row items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <AppWindow className="h-5 w-5 text-primary" />
                <h1 className="text-ui-2xl font-bold tracking-tight text-foreground">
                  App Builder
                </h1>
                <Badge
                  variant="outline"
                  className="border-primary/30 text-primary text-ui-xs uppercase tracking-wider"
                >
                  Beta
                </Badge>
              </div>
              <p className="text-ui-base text-muted-foreground mt-1">
                Describe what you want, upload your data, get a sandboxed
                interactive app.
              </p>
              <ContextualHelpLink topic="app-builder" label="How does this work?" className="mt-2" />
            </div>
            <Button asChild className="gap-1.5 shrink-0" data-tour-new-app-button="">
              <Link to="/apps/new">
                <Plus className="h-4 w-4" />
                New app
              </Link>
            </Button>
          </div>

          {/* Tip banner */}
          <div className="mb-6 rounded-ui-md border border-primary/20 bg-primary/5 p-4">
            <div className="flex gap-3">
              <Lightbulb className="h-4 w-4 shrink-0 text-primary mt-0.5" />
              <div className="text-ui-sm text-muted-foreground leading-relaxed">
                <p className="font-bold text-foreground mb-1">
                  What is App Builder?
                </p>
                <p>
                  Upload a CSV or Excel file and describe the dashboard or
                  interactive app you want. AskBell generates a real,
                  sandboxed app you can save, revisit, and refine with AI.
                  There are two output types: <strong>Dashboard</strong>{" "}
                  (charts and tables built from a fixed widget library — fast
                  and reliable) and <strong>Interactive app</strong> (a fully
                  custom React app rendered safely in an isolated iframe).
                  AskBell picks the right one for your request.
                </p>
              </div>
            </div>
          </div>

          {/* Demo links */}
          <div className="mb-4 flex justify-end gap-2">
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="text-ui-sm text-muted-foreground hover:text-foreground gap-1.5"
            >
              <Link to="/apps/demo">
                <Sparkles className="h-3 w-3" />
                Spec renderer demo
              </Link>
            </Button>
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="text-ui-sm text-muted-foreground hover:text-foreground gap-1.5"
            >
              <Link to="/apps/demo-react">
                <Code2 className="h-3 w-3" />
                React renderer demo
              </Link>
            </Button>
          </div>

          {/* Search + filter */}
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                aria-label="Search apps"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search your apps…"
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-1 rounded-ui-sm border border-border p-1 bg-card">
              {(
                [
                  { id: "all", label: "All" },
                  { id: "spec", label: "Dashboards" },
                  { id: "react", label: "Interactive" },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setKindFilter(opt.id)}
                  aria-pressed={kindFilter === opt.id}
                  className={`px-2.5 py-1 rounded text-ui-sm transition-colors ${
                    kindFilter === opt.id
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          {loadError ? <div role="alert" className="rounded-ui-md border border-destructive/30 p-5"><p className="text-ui-base">{loadError}</p><Button className="mt-3" variant="outline" disabled={loading} onClick={() => void fetchRows()}>Retry apps</Button></div> : loading ? (
            <div className="flex justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : visible.length === 0 ? (
            <EmptyState hasAny={(rows ?? []).length > 0} />
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {visible.map((app) => (
                <li key={app.id} className="group/app">
                  <Link
                    to="/apps/$appId"
                    params={{ appId: app.id }}
                    className="relative block rounded-ui-md border border-border bg-card hover:border-primary/40 hover:bg-secondary/30 transition-colors p-4"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {app.kind === "react" ? (
                          <Code2 className="h-4 w-4 text-primary shrink-0" />
                        ) : (
                          <Sparkles className="h-4 w-4 text-primary shrink-0" />
                        )}
                        <span className="font-bold text-foreground truncate">
                          {app.title}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={(e) => toggleStar(app, e)}
                          disabled={starring.has(app.id)}
                          aria-pressed={app.starred}
                          aria-label={app.starred ? "Unstar" : "Star"}
                          className={`p-1 rounded hover:bg-secondary ${
                            app.starred
                              ? "text-ui-warning-icon opacity-100"
                              : "text-muted-foreground md:opacity-0 group-hover/app:opacity-100 focus-visible:opacity-100"
                          } transition-opacity`}
                        >
                          <Star
                            className={`h-3.5 w-3.5 ${app.starred ? "fill-current" : ""}`}
                          />
                        </button>
                        <button
                          onClick={(e) => requestDelete(app, e)}
                          aria-label="Delete app"
                          className="p-1 rounded hover:bg-secondary text-muted-foreground md:opacity-0 group-hover/app:opacity-100 focus-visible:opacity-100 hover:text-destructive transition-opacity"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    {app.description && (
                      <p className="text-ui-sm text-muted-foreground mt-1.5 line-clamp-2 ml-6">
                        {app.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-3 ml-6">
                      <Badge
                        variant="outline"
                        className="text-ui-xs uppercase tracking-wider"
                      >
                        {app.kind === "react" ? "Interactive" : "Dashboard"}
                      </Badge>
                      <span className="text-ui-xs text-muted-foreground">
                        Updated {timeAgo(app.updated_at)}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open && !deleteLock.current) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this app?</AlertDialogTitle>
            <AlertDialogDescription>
              "{pendingDelete?.title}" and all of its versions will be
              permanently removed. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

function EmptyState({ hasAny }: { hasAny: boolean }) {
  return (
    <div className="text-center py-16 text-muted-foreground">
      <AppWindow className="h-10 w-10 mx-auto mb-3 opacity-40" />
      {hasAny ? (
        <p className="text-ui-base">No apps match your search.</p>
      ) : (
        <>
          <p className="text-ui-base font-bold text-foreground mb-1">
            No apps yet
          </p>
          <p className="text-ui-sm max-w-md mx-auto mb-4">
            Create your first app from a CSV or Excel file. AskBell will turn
            your prompt into a real interactive dashboard.
          </p>
          <Button asChild className="gap-1.5">
            <Link to="/apps/new">
              <Plus className="h-4 w-4" />
              New app
            </Link>
          </Button>
        </>
      )}
    </div>
  );
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}
